import React, { useState, useMemo, useRef } from 'react';
import axiosInstance from '../../api/axiosConfig';
import {
    PAYMENT_TYPES,
    CURRENCIES,
    USD_TO_QAR,
    formatAmount,
    formatDate,
    formatDateShort,
    toDateTimeInput,
    fromDateTimeInput,
    isRecurring,
    costCentreId
} from './paymentConstants';
import { CardPicker, CostCentreSelect } from './PaymentPickers';
import ServiceAvatar from './ServiceAvatar';

// Record a charge — or correct one already recorded (`payment` set).
//
// Built for the moment the bank SMS arrives: the amount is pre-filled from the
// service, the card and branch from the service, the time is "now", and the
// only thing usually left is pressing Save. Opened without a `record` it starts
// with a service picker, which is what the header's "Record charge" button does.

const RecordPaymentModal = ({
    record: initialRecord, payment, records = [], lookups, defaultCardId,
    canAddCard, onCardCreated, onClose, onSaved
}) => {
    const editing = Boolean(payment);
    const [record, setRecord] = useState(initialRecord || null);
    const [form, setForm] = useState(() => {
        if (editing) {
            return {
                amount: payment.amount ?? '',
                paidDate: toDateTimeInput(payment.paidDate),
                reference: payment.reference || '',
                notes: payment.notes || '',
                status: payment.status,
                failureReason: payment.failureReason || '',
                card: String(payment.card?._id || payment.card || ''),
                costCentre: costCentreId(payment),
                originalAmount: payment.originalAmount ?? '',
                originalCurrency: payment.originalCurrency || 'USD',
                currency: payment.currency || 'QAR'
            };
        }
        return initialForm(initialRecord, defaultCardId);
    });
    const [receipt, setReceipt] = useState(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const cardPickerRef = useRef(null);

    const set = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

    const pickRecord = (id) => {
        const next = records.find(r => r._id === id) || null;
        setRecord(next);
        setForm(prev => ({ ...initialForm(next, defaultCardId), paidDate: prev.paidDate }));
    };

    const recurring = record ? isRecurring(record) : false;
    // A new charge is in the service's currency; an edit may correct it
    const currency = editing ? form.currency : (record?.currency || 'QAR');

    // Services you can record against, most-used kinds first
    const pickable = useMemo(() => {
        const live = records.filter(r => ['active', 'trial'].includes(r.status) && r.paymentType !== 'onetime');
        return [...live].sort((a, b) => a.name.localeCompare(b.name));
    }, [records]);

    const timePresets = () => {
        const now = new Date();
        const yesterday = new Date(now);
        yesterday.setDate(yesterday.getDate() - 1);
        const presets = [
            { label: 'Now', value: toDateTimeInput(now) },
            { label: 'Yesterday', value: toDateTimeInput(yesterday) }
        ];
        if (recurring && record?.nextDueDate && !editing) {
            presets.push({ label: `Due date (${formatDateShort(record.nextDueDate)})`, value: toDateTimeInput(record.nextDueDate) });
        }
        return presets;
    };

    // QAR is pegged to the dollar, so a vendor price in USD gives a near-exact
    // suggestion for the card figure. It is only a suggestion — the statement wins.
    const suggestion = form.originalAmount && form.originalCurrency === 'USD' && currency === 'QAR'
        ? (Number(form.originalAmount) * USD_TO_QAR).toFixed(2)
        : null;

    // Correcting a USD payment to QAR: the figure typed is still the dollar
    // one, so offer the conversion and keep the dollars as the vendor price.
    const convertFromUsd = editing && payment.currency === 'USD' && currency === 'QAR'
        && form.amount !== '' && Number(form.amount) === Number(payment.amount)
        ? (Number(payment.amount) * USD_TO_QAR).toFixed(2)
        : null;

    const applyUsdConversion = () => setForm(prev => ({
        ...prev,
        amount: convertFromUsd,
        ...(prev.originalAmount === '' ? { originalAmount: payment.amount, originalCurrency: 'USD' } : {})
    }));

    const submitCreate = async (card, allowDuplicate) => {
        const data = new FormData();
        data.append('amount', Number(form.amount));
        data.append('paidDate', fromDateTimeInput(form.paidDate));
        data.append('reference', form.reference);
        data.append('notes', form.notes);
        data.append('status', form.status);
        data.append('failureReason', form.failureReason);
        data.append('card', card);
        data.append('costCentre', form.costCentre);
        if (form.originalAmount !== '') {
            data.append('originalAmount', form.originalAmount);
            data.append('originalCurrency', form.originalCurrency);
        }
        if (allowDuplicate) data.append('allowDuplicate', 'true');
        if (receipt) data.append('receipt', receipt);
        await axiosInstance.post(`/api/subscriptions/${record._id}/payments`, data);
    };

    const submitEdit = async (card) => {
        await axiosInstance.put(
            `/api/subscriptions/${payment.subscription?._id || payment.subscription}/payments/${payment._id}`,
            {
                amount: Number(form.amount),
                currency: form.currency,
                paidDate: fromDateTimeInput(form.paidDate),
                reference: form.reference,
                notes: form.notes,
                card,
                costCentre: form.costCentre,
                originalAmount: form.originalAmount === '' ? null : form.originalAmount,
                originalCurrency: form.originalCurrency
            }
        );
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!editing && !record) {
            setError('Choose which service this charge is for');
            return;
        }
        setError('');
        setSaving(true);
        // A card typed into the add-card form but not yet added is added now
        const pendingCard = await cardPickerRef.current?.commitPending();
        if (pendingCard === null) {
            setSaving(false);
            setError('The new card could not be added — fix it above, or press Cancel on it');
            return;
        }
        const card = pendingCard ?? form.card;
        try {
            if (editing) await submitEdit(card);
            else await submitCreate(card, false);
            onSaved();
        } catch (err) {
            // The backend blocks an identical same-day payment because that is
            // almost always a double-submit. Prepaying two cycles on one day is
            // legitimate though, so ask rather than making it impossible.
            if (!editing && err.response?.data?.duplicate
                && window.confirm(`${err.response.data.message}\n\nRecord it anyway?`)) {
                try {
                    await submitCreate(card, true);
                    onSaved();
                    return;
                } catch (retryErr) {
                    setError(retryErr.response?.data?.message || 'Failed to record payment');
                    return;
                }
            }
            setError(err.response?.data?.message || (editing ? 'Failed to update payment' : 'Failed to record payment'));
        } finally {
            setSaving(false);
        }
    };

    const title = editing
        ? `Edit payment — ${payment.subscriptionName}`
        : record ? `Record charge — ${record.name}` : 'Record a charge';

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>{title}</h2>
                    <button className="modal-close" onClick={onClose}>×</button>
                </div>
                <form onSubmit={handleSubmit}>
                    <div className="modal-body">
                        {!editing && !initialRecord && (
                            <div className="form-group">
                                <label className="form-label">Which service? *</label>
                                <select className="form-select" value={record?._id || ''} onChange={(e) => pickRecord(e.target.value)} autoFocus required>
                                    <option value="">
                                        {pickable.length === 0 ? '— No running services yet —' : '— Choose a service —'}
                                    </option>
                                    {['usage', 'subscription'].map(type => {
                                        const items = pickable.filter(r => (r.paymentType || 'subscription') === type);
                                        if (items.length === 0) return null;
                                        return (
                                            <optgroup key={type} label={`${PAYMENT_TYPES[type].icon} ${PAYMENT_TYPES[type].label}`}>
                                                {items.map(r => (
                                                    <option key={r._id} value={r._id}>
                                                        {r.name}{r.amount != null ? ` — ${formatAmount(r.amount, r.currency)}` : ''}
                                                    </option>
                                                ))}
                                            </optgroup>
                                        );
                                    })}
                                </select>
                                {pickable.length === 0 && (
                                    <span className="form-hint">No running services yet — add one first with "+ Add service".</span>
                                )}
                            </div>
                        )}

                        {(record || editing) && (
                            <div className="summary-box">
                                <ServiceAvatar record={record || payment.subscription || { name: payment.subscriptionName }} size="sm" />
                                <span>
                                    {editing
                                        ? 'Correcting the details only — the renewal schedule is not touched. Use "Mark failed" to change whether it went through.'
                                        : recurring
                                            ? `Covers the ${formatDate(record.nextDueDate)} renewal. After saving, the next renewal moves forward one cycle automatically.`
                                            : 'No schedule — this just logs the charge. Nothing is rescheduled.'}
                                </span>
                            </div>
                        )}
                        {error && <div className="alert alert-error">{error}</div>}

                        {!editing && (
                            <div className="chip-row chip-row-tight">
                                <span className="template-label">Outcome:</span>
                                <button
                                    type="button"
                                    className={`chip chip-sm ${form.status === 'paid' ? 'chip-active' : ''}`}
                                    onClick={() => set('status', 'paid')}
                                >
                                    ✓ Charged / paid
                                </button>
                                <button
                                    type="button"
                                    className={`chip chip-sm ${form.status === 'failed' ? 'chip-active chip-danger' : ''}`}
                                    onClick={() => set('status', 'failed')}
                                >
                                    ✕ Payment failed
                                </button>
                            </div>
                        )}
                        {form.status === 'failed' && !editing && (
                            <div className="form-group">
                                <label className="form-label">What went wrong? <span className="form-hint">(the renewal date will not move)</span></label>
                                <input
                                    className="form-input"
                                    value={form.failureReason}
                                    placeholder="e.g. card declined, card expired"
                                    onChange={(e) => set('failureReason', e.target.value)}
                                />
                            </div>
                        )}

                        <div className="form-grid">
                            <div className="form-group">
                                <label className="form-label">
                                    Amount charged * <span className="form-hint">({currency}, as on the statement)</span>
                                </label>
                                {editing ? (
                                    <div className="amount-row">
                                        <input
                                            className="form-input amount-input-lg" type="number" min="0" step="0.01" required
                                            autoFocus
                                            value={form.amount}
                                            placeholder="0.00"
                                            onChange={(e) => set('amount', e.target.value)}
                                        />
                                        <select
                                            className="form-select currency-select"
                                            value={form.currency}
                                            onChange={(e) => set('currency', e.target.value)}
                                            aria-label="Currency charged"
                                        >
                                            {(CURRENCIES.includes(form.currency) ? CURRENCIES : [form.currency, ...CURRENCIES])
                                                .map(c => <option key={c} value={c}>{c}</option>)}
                                        </select>
                                    </div>
                                ) : (
                                    <input
                                        className="form-input amount-input-lg" type="number" min="0" step="0.01" required
                                        autoFocus={Boolean(initialRecord)}
                                        value={form.amount}
                                        placeholder="0.00"
                                        onChange={(e) => set('amount', e.target.value)}
                                    />
                                )}
                                {convertFromUsd && (
                                    <button type="button" className="link-button" onClick={applyUsdConversion}>
                                        Convert USD {payment.amount} → QAR {convertFromUsd} (× {USD_TO_QAR}, keeps USD as vendor price)
                                    </button>
                                )}
                                {!convertFromUsd && suggestion && suggestion !== String(form.amount) && (
                                    <button type="button" className="link-button" onClick={() => set('amount', suggestion)}>
                                        Use ≈ QAR {suggestion} (USD × {USD_TO_QAR})
                                    </button>
                                )}
                            </div>
                            <div className="form-group">
                                <label className="form-label">Date &amp; time *</label>
                                <input
                                    className="form-input" type="datetime-local" required
                                    value={form.paidDate}
                                    onChange={(e) => set('paidDate', e.target.value)}
                                />
                                <div className="chip-row chip-row-tight">
                                    {timePresets().map(p => (
                                        <button key={p.label} type="button" className="chip chip-sm" onClick={() => set('paidDate', p.value)}>
                                            {p.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div className="form-group">
                            <label className="form-label">Paid with</label>
                            <CardPicker
                                ref={cardPickerRef}
                                cards={lookups?.cards || []}
                                value={form.card}
                                onChange={(id) => set('card', id)}
                                onCardCreated={onCardCreated}
                                canAddCard={canAddCard}
                                otherLabel="Not a card"
                            />
                        </div>

                        <div className="form-grid">
                            <div className="form-group">
                                <label className="form-label">Charged to (company / branch)</label>
                                <CostCentreSelect
                                    costCentres={lookups?.costCentres || []}
                                    value={form.costCentre}
                                    onChange={(id) => set('costCentre', id)}
                                />
                            </div>
                            <div className="form-group">
                                <label className="form-label">
                                    Vendor price <span className="form-hint">(optional — e.g. USD 20 on the invoice)</span>
                                </label>
                                <div className="amount-row">
                                    <input
                                        className="form-input" type="number" min="0" step="0.01"
                                        value={form.originalAmount}
                                        placeholder="0.00"
                                        onChange={(e) => set('originalAmount', e.target.value)}
                                    />
                                    <select className="form-select currency-select" value={form.originalCurrency} onChange={(e) => set('originalCurrency', e.target.value)}>
                                        {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
                                    </select>
                                </div>
                            </div>
                        </div>

                        <div className="form-grid">
                            <div className="form-group">
                                <label className="form-label">Reference</label>
                                <input
                                    className="form-input"
                                    value={form.reference}
                                    placeholder="Invoice / receipt / transaction no."
                                    onChange={(e) => set('reference', e.target.value)}
                                />
                            </div>
                            {!editing && (
                                <div className="form-group">
                                    <label className="form-label">Invoice / receipt <span className="form-hint">(saved to SharePoint — more can be attached later)</span></label>
                                    <input
                                        className="form-input" type="file"
                                        accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx,.xls,.xlsx"
                                        onChange={(e) => setReceipt(e.target.files?.[0] || null)}
                                    />
                                </div>
                            )}
                        </div>
                        <div className="form-group">
                            <label className="form-label">Notes</label>
                            <textarea
                                className="form-textarea" rows={2}
                                value={form.notes}
                                onChange={(e) => set('notes', e.target.value)}
                            />
                        </div>
                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
                        <button type="submit" className={`btn ${form.status === 'failed' ? 'btn-danger' : 'btn-success'}`} disabled={saving}>
                            {saving
                                ? 'Saving...'
                                : editing ? 'Save changes' : form.status === 'failed' ? 'Record failed payment' : 'Save payment'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// Pre-fill from the service: its expected amount, its card (or the last card
// used), its branch, and the current time.
function initialForm(record, defaultCardId) {
    return {
        amount: record && record.paymentType !== 'usage' ? (record.amount ?? '') : '',
        paidDate: toDateTimeInput(new Date()),
        reference: '',
        notes: '',
        status: 'paid',
        failureReason: '',
        card: String(record?.card?._id || record?.card || defaultCardId || ''),
        costCentre: record ? costCentreId(record) : '',
        originalAmount: '',
        originalCurrency: 'USD'
    };
}

export default RecordPaymentModal;
